import { useEffect, useState } from 'react';
import { Box, DialogActions, DialogContent, Typography } from '@mui/material';
import { CarrierServicingGrantInputDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useQueryClient } from 'react-query';
import { Modal } from '../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../ui/components/pure/text-button/text-button.component';
import { parseErrorMessage } from '../../../core/api/api.context';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { useQueryListNetworkProjects } from '../../../core/queries/projects.queries';
import { useQueryListEffectiveServicing } from '../carriers.queries';
import { useMutationGrantServicing, useMutationRevokeServicing } from '../carrier-organisations.queries';
import { diffServicing, hasServicingChanges, toDirectServicing } from '../logic/servicing-diff';
import { NetworksStep } from '../add-carrier-modal/steps/networks-step';

export interface AssignNetworksModalProps {
  /** The group holding the grants: an organisation's container, or a pool carrier. */
  groupID: string;
  carrierName?: string;
  description?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function AssignNetworksModal({
  groupID,
  carrierName,
  description,
  onSuccess,
  onCancel
}: AssignNetworksModalProps) {
  const [servicing, setServicing] = useState<CarrierServicingGrantInputDto[]>([]);
  const [error, setError] = useState<string | undefined>();

  const networks = useQueryListNetworkProjects();
  const effective = useQueryListEffectiveServicing(groupID);
  const grantServicing = useMutationGrantServicing();
  const revokeServicing = useMutationRevokeServicing();
  const queryClient = useQueryClient();

  const current = toDirectServicing(effective.data ?? []);

  // Seeded once the grants arrive; edits after that are the user's.
  const [isSeeded, setIsSeeded] = useState(false);
  useEffect(() => {
    if (!isSeeded && effective.isSuccess) {
      setServicing(toDirectServicing(effective.data ?? []));
      setIsSeeded(true);
    }
  }, [isSeeded, effective.isSuccess, effective.data]);

  const change = diffServicing(current, servicing);
  const isSaving = grantServicing.isLoading || revokeServicing.isLoading;
  const isLoading = networks.isLoading || effective.isLoading;

  const onSave = async () => {
    setError(undefined);

    try {
      // Grants first: a network being narrowed keeps working throughout.
      for (const grant of change.grants) {
        await grantServicing.mutateAsync({ groupID, grant });
      }
      for (const grant of change.revokes) {
        await revokeServicing.mutateAsync({ groupID, grant });
      }
    } catch (e) {
      // Granting is idempotent per permission, so retrying the whole set is safe.
      setError(parseErrorMessage((e as RestError).message) || 'Could not save the network changes.');
      return;
    }

    queryClient.invalidateQueries(QueryKeys.CARRIERS);
    onSuccess();
  };

  return (
    <Modal
      title="Assign Networks"
      open
      divider
      maxWidth="sm"
      loading={isLoading}
      onClose={isSaving ? undefined : onCancel}
    >
      <DialogContent style={{ padding: '16px 24px' }}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {description ??
            `Where ${carrierName ?? 'this carrier'} may work. Its regions inherit whatever networks are set here.`}
        </Typography>

        <NetworksStep
          networks={networks.data ?? []}
          isLoading={isLoading}
          loadError={
            networks.isError || effective.isError
              ? parseErrorMessage(networks.error?.message ?? effective.error?.message) || 'Could not load the networks.'
              : undefined
          }
          servicing={servicing}
          onChange={setServicing}
          disabled={isSaving}
        />

        {error && (
          <Box
            sx={{
              marginTop: 2
            }}
          >
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Nothing is applied twice, so it is safe to try again.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="primary" variant="outlined" onClick={onCancel} disabled={isSaving}>
          Cancel
        </TextButton>
        <TextButton
          color="primary"
          variant="contained"
          onClick={onSave}
          isLoading={isSaving}
          disabled={isSaving || isLoading || !hasServicingChanges(change)}
        >
          Save
        </TextButton>
      </DialogActions>
    </Modal>
  );
}
